import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Copy, Check } from "lucide-react";
import { fetchAnnouncementsPublic } from "../../services/cmsClient";
import { useVendorStore } from "../../context/VendorStoreContext";

const toneStyles = {
  info: {
    shell: "from-slate-950 via-slate-900 to-slate-800 text-white",
    badge: "bg-white/12 text-sky-100 ring-white/20",
    cta: "bg-white text-slate-950 hover:bg-slate-100",
    close: "text-white/75 hover:bg-white/12 hover:text-white",
  },
  success: {
    shell: "from-emerald-700 via-teal-700 to-cyan-800 text-white",
    badge: "bg-white/14 text-emerald-50 ring-white/20",
    cta: "bg-white text-emerald-900 hover:bg-emerald-50",
    close: "text-white/75 hover:bg-white/12 hover:text-white",
  },
  warning: {
    shell: "from-amber-100 via-orange-100 to-rose-100 text-amber-950",
    badge: "bg-white/70 text-amber-900 ring-amber-200",
    cta: "bg-amber-950 text-white hover:bg-amber-900",
    close: "text-amber-900/70 hover:bg-amber-900/10 hover:text-amber-950",
  },
  danger: {
    shell: "from-rose-700 via-red-700 to-orange-700 text-white",
    badge: "bg-white/14 text-rose-50 ring-white/20",
    cta: "bg-white text-rose-900 hover:bg-rose-50",
    close: "text-white/75 hover:bg-white/12 hover:text-white",
  },
};

async function copyText(text) {
  const value = String(text || "").trim();
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
}

export default function AnnouncementBar() {
  const { vendorStore } = useVendorStore();
  const vendorId = vendorStore?._id || null;

  const [announcements, setAnnouncements] = useState([]);
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("dismissedAnnouncementId") || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    let alive = true;
    fetchAnnouncementsPublic(vendorId)
      .then((items) => {
        if (alive) setAnnouncements(items);
      })
      .catch(() => {
        if (alive) setAnnouncements([]);
      });
    return () => {
      alive = false;
    };
  }, [vendorId]);

  const item = useMemo(
    () => announcements.find((a) => String(a._id) !== dismissed),
    [announcements, dismissed],
  );

  useEffect(() => {
    setCopied(false);
  }, [item?._id]);

  if (!item) return null;

  const styles = toneStyles[item.tone] || toneStyles.info;
  const couponCode = String(item.couponCode || "").trim();
  const copyLabel =
    String(item.copyButtonLabel || "").trim() || "Copy code";

  const content = (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left sm:flex-row sm:items-center sm:justify-center sm:gap-2 sm:text-center">
      <span className="truncate text-sm font-extrabold tracking-tight sm:text-[15px]">
        {item.title}
      </span>
      {item.message ? (
        <span className="line-clamp-2 text-xs font-medium opacity-90 sm:text-sm">
          {item.message}
        </span>
      ) : null}
    </div>
  );

  const linkCta =
    item.linkUrl && item.linkLabel ? (
      item.linkUrl.startsWith("/") ? (
        <Link
          to={item.linkUrl}
          className={`inline-flex shrink-0 items-center rounded-full px-3.5 py-1.5 text-xs font-bold shadow-sm transition ${styles.cta}`}
        >
          {item.linkLabel}
        </Link>
      ) : (
        <a
          href={item.linkUrl}
          className={`inline-flex shrink-0 items-center rounded-full px-3.5 py-1.5 text-xs font-bold shadow-sm transition ${styles.cta}`}
          rel="noreferrer"
          target="_blank"
        >
          {item.linkLabel}
        </a>
      )
    ) : null;

  const handleCopyCoupon = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyText(couponCode);
    if (ok) {
      setCopied(true);
      toast.success(`Copied ${couponCode}`);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Could not copy. Please copy manually.");
    }
  };

  const copyCta = couponCode ? (
    <button
      type="button"
      onClick={handleCopyCoupon}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold shadow-sm transition ${styles.cta}`}
      aria-label={`Copy coupon code ${couponCode}`}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden />
      )}
      {copied ? "Copied!" : copyLabel}
    </button>
  ) : null;

  const dismiss = () => {
    const id = String(item._id);
    setDismissed(id);
    try {
      sessionStorage.setItem("dismissedAnnouncementId", id);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={`relative overflow-hidden bg-gradient-to-r ${styles.shell}`}>
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_50%,rgba(255,255,255,0.22),transparent_28%),radial-gradient(circle_at_85%_50%,rgba(255,255,255,0.16),transparent_24%)]"
        aria-hidden
      />
      <div className="relative mx-auto flex max-w-7xl items-center gap-3 px-3 py-2.5 sm:px-6">
        <span
          className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 sm:inline-flex ${styles.badge}`}
          aria-hidden
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 8v4l2.5 2.5M12 3.75A8.25 8.25 0 1020.25 12 8.25 8.25 0 0012 3.75z"
            />
          </svg>
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-2 sm:gap-3">
          {content}
          {linkCta}
          {copyCta}
        </div>
        {item.dismissible !== false ? (
          <button
            type="button"
            onClick={dismiss}
            className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition ${styles.close}`}
            aria-label="Dismiss announcement"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6l12 12M18 6L6 18"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}
